/**
 * BrowserVoiceSession — the client for mic v2 (in-browser streaming voice).
 *
 * Captures the mic as PCM16, streams it over a websocket to the backend
 * (/api/webhooks/voice-agent/browser-stream), which runs Deepgram STT + the agent +
 * Deepgram Aura TTS and streams audio back. We play that audio and surface live
 * transcript captions. Barge-in: a {clear} from the server flushes queued playback.
 *
 * Echo cancellation: the mic requests echoCancellation, and the agent's TTS is played
 * through a MediaStream routed to an <audio> element — not straight to the AudioContext
 * destination — so the browser's echo canceller uses it as the reference and keeps the
 * agent's own voice out of the mic. That lets the server re-enable true barge-in.
 *
 * Best-effort: any failure calls onState('error'); the Dock then falls back to mic v1
 * (browser SpeechRecognition). No external deps — Web Audio + WebSocket only.
 */

export type VoiceState = 'connecting' | 'live' | 'listening' | 'speaking' | 'ended' | 'error';

export interface VoiceOptions {
  /**
   * A bearer token sent as the FIRST frame, before `start`.
   *
   * The browser WebSocket API cannot set request headers, so a socket has no
   * Authorization. WeaveIn's voice sockets carry a real healthcare principal —
   * the turn runs as the user recorded on the session — and the session id
   * alone travels in the URL, where it joins browser history and proxy logs.
   * This frame proves the caller holds the user's own credentials.
   *
   * Optional: Triveny's design-studio sessions carry no actor and the server
   * does not ask them for one. Omitting it there keeps that path unchanged.
   */
  authToken?: string;
}

/** A gated action the agent proposed, waiting on a human. */
export interface VoiceApproval {
  /** The SERVER's id. Confirming sends this and nothing else — the arguments
   *  stay on the server, so what runs is what was shown. */
  id: string;
  tool: string;
  args: Record<string, unknown>;
  gate?: string;
  reversibility?: string;
  message?: string;
}

export interface VoiceCallbacks {
  onCaption?: (role: 'user' | 'agent', text: string) => void;
  onState?: (state: VoiceState) => void;
  /** Fired when the agent built something with an id (e.g. a page_id) mid-conversation. */
  onArtifact?: (pageId: string) => void;
  /** A gated action needs a human. Render a card; call `confirm(id)` on yes. */
  onApproval?: (approval: VoiceApproval) => void;
  /** How that action turned out, once it ran. */
  onApprovalResult?: (id: string, ok: boolean, result: Record<string, unknown>) => void;
}

export class BrowserVoiceSession {
  private ws?: WebSocket;
  private ctx?: AudioContext;
  private stream?: MediaStream;
  private processor?: ScriptProcessorNode;
  private source?: MediaStreamAudioSourceNode;
  private playCursor = 0;
  private playing: AudioBufferSourceNode[] = [];
  private stopped = false;
  // Playback is routed through a MediaStream + <audio> element so the browser echo
  // canceller references it (raw ctx.destination output leaks into the mic). Falls back
  // to ctx.destination if MediaStream playback can't be set up.
  private playDest?: MediaStreamAudioDestinationNode;
  private playEl?: HTMLAudioElement;
  private playTarget?: AudioNode;
  //
  // THE SERVER CANNOT KNOW THIS.
  //
  // It stops "speaking" the moment it has SENT the last audio frame. We queue
  // those frames against `playCursor` and play them over the following seconds
  // — half a minute, for a long answer. So the server saw speaking=false while
  // the user was still listening, took the ordinary path when they interrupted,
  // and never sent {clear}: the queued audio played on to the end. That is
  // exactly what "barge-in doesn't stop it" looks like.
  //
  // Only the browser knows when sound actually stops, so the browser says so.
  private reportedPlaying = false;

  constructor(
    private wsUrl: string,
    private cb: VoiceCallbacks,
    private opts: VoiceOptions = {},
  ) {}

  async start(): Promise<void> {
    this.cb.onState?.('connecting');
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      this.cb.onState?.('error');
      return;
    }
    const AC: typeof AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
    this.ctx = new AC();
    const rate = Math.round(this.ctx.sampleRate);

    // Route TTS playback through a MediaStream + <audio> element so the mic's echo
    // canceller uses it as its reference. If anything here fails, fall back to the
    // AudioContext destination (echo cancellation then relies on the OS alone).
    try {
      this.playDest = this.ctx.createMediaStreamDestination();
      this.playEl = new Audio();
      this.playEl.srcObject = this.playDest.stream;
      this.playEl.autoplay = true;
      void this.playEl.play().catch(() => {});
      this.playTarget = this.playDest;
    } catch {
      this.playTarget = this.ctx.destination;
    }

    this.ws = new WebSocket(this.wsUrl);
    this.ws.binaryType = 'arraybuffer';
    this.ws.onopen = () => {
      try {
        // Auth BEFORE start. The server reads exactly one frame and expects
        // this one; anything else closes the socket. Sending it first is also
        // why this is safe to deploy ahead of the server change — the old
        // handshake loop skips frames until it sees `start`.
        if (this.opts.authToken) {
          this.ws!.send(JSON.stringify({ type: 'auth', token: this.opts.authToken }));
        }
        this.ws!.send(JSON.stringify({ type: 'start', sample_rate: rate }));
      } catch {
        /* noop */
      }
      this.startCapture();
      this.cb.onState?.('live');
    };
    this.ws.onmessage = (e) => this.onMessage(e);
    this.ws.onclose = () => {
      this.cb.onState?.('ended');
      this.cleanup();
    };
    this.ws.onerror = () => this.cb.onState?.('error');
  }

  private startCapture(): void {
    if (!this.ctx || !this.stream) return;
    const src = this.ctx.createMediaStreamSource(this.stream);
    const proc = this.ctx.createScriptProcessor(4096, 1, 1);
    proc.onaudioprocess = (e: AudioProcessingEvent) => {
      if (this.stopped || this.ws?.readyState !== WebSocket.OPEN) return;
      const f32 = e.inputBuffer.getChannelData(0);
      const i16 = new Int16Array(f32.length);
      for (let i = 0; i < f32.length; i++) {
        const s = Math.max(-1, Math.min(1, f32[i]));
        i16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      try {
        this.ws!.send(i16.buffer);
      } catch {
        /* noop */
      }
    };
    src.connect(proc);
    proc.connect(this.ctx.destination);
    this.source = src;
    this.processor = proc;
  }

  private onMessage(e: MessageEvent): void {
    if (typeof e.data === 'string') {
      let m: any;
      try {
        m = JSON.parse(e.data);
      } catch {
        return;
      }
      if (m.type === 'transcript') this.cb.onCaption?.(m.role, m.text);
      else if (m.type === 'artifact' && m.page_id) this.cb.onArtifact?.(String(m.page_id));
      else if (m.type === 'speaking') this.cb.onState?.(m.on ? 'speaking' : 'listening');
      else if (m.type === 'clear') this.flushPlayback();
      else if (m.type === 'approval' && m.id) this.cb.onApproval?.(m as VoiceApproval);
      else if (m.type === 'approval_result' && m.id)
        this.cb.onApprovalResult?.(String(m.id), Boolean(m.ok), (m.result as any) || {});
      else if (m.type === 'end') this.stop();
      // The server refused the auth frame: an expired token, or a session
      // that belongs to someone else. Same visible outcome as 'unavailable' —
      // the panel says voice could not start — but logged distinctly, because
      // "your session expired" and "speech is not configured" are different
      // problems with different fixes.
      else if (m.type === 'unauthorized') {
        this.cb.onState?.('error');
        this.stop();
      } else if (m.type === 'unavailable') {
        this.cb.onState?.('error');
        this.stop();
      }
      return;
    }
    this.playPcm(e.data as ArrayBuffer);
  }

  private playPcm(buf: ArrayBuffer): void {
    if (!this.ctx) return;
    const i16 = new Int16Array(buf);
    const f32 = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 0x8000;
    const ab = this.ctx.createBuffer(1, f32.length, 24000);
    ab.getChannelData(0).set(f32);
    const src = this.ctx.createBufferSource();
    src.buffer = ab;
    src.connect(this.playTarget ?? this.ctx.destination);
    const now = this.ctx.currentTime;
    const startAt = Math.max(now, this.playCursor);
    try {
      src.start(startAt);
    } catch {
      return;
    }
    this.playCursor = startAt + ab.duration;
    this.playing.push(src);
    this.setPlaying(true);
    src.onended = () => {
      this.playing = this.playing.filter((s) => s !== src);
      if (this.playing.length === 0) this.setPlaying(false);
    };
  }

  private flushPlayback(): void {
    this.playing.forEach((s) => {
      try {
        s.stop();
      } catch {
        /* noop */
      }
    });
    this.playing = [];
    this.playCursor = this.ctx?.currentTime ?? 0;
    this.setPlaying(false);
  }

  /** Tell the server whether sound is coming out of the speakers right now.
   *  Edge-triggered: one frame per transition, not one per buffer. */
  private setPlaying(on: boolean): void {
    if (this.reportedPlaying === on) return;
    this.reportedPlaying = on;
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify({ type: 'playing', on }));
    } catch {
      /* the socket is going away; the server treats absence as not playing */
    }
  }

  /**
   * Confirm a gated action.
   *
   * The id and nothing else. The server holds the tool and the arguments it
   * showed, so there is no path by which the action that RUNS differs from the
   * one on the card — a stronger guarantee than the typed surface, which takes
   * the arguments back from the client.
   */
  confirm(id: string): void {
    try {
      this.ws?.send(JSON.stringify({ type: 'confirm', id }));
    } catch {
      /* the socket is going away; nothing runs, which is the safe outcome */
    }
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    try {
      this.ws?.send(JSON.stringify({ type: 'stop' }));
    } catch {
      /* noop */
    }
    this.cleanup();
  }

  private cleanup(): void {
    try {
      this.processor?.disconnect();
    } catch {
      /* noop */
    }
    try {
      this.source?.disconnect();
    } catch {
      /* noop */
    }
    try {
      this.stream?.getTracks().forEach((t) => t.stop());
    } catch {
      /* noop */
    }
    try {
      if (this.playEl) {
        this.playEl.pause();
        this.playEl.srcObject = null;
      }
    } catch {
      /* noop */
    }
    try {
      this.ctx?.close();
    } catch {
      /* noop */
    }
    try {
      this.ws?.close();
    } catch {
      /* noop */
    }
  }
}
